/*
 * File: Login.js
 * Project: patient-portal
 *
 * Created by Nate Weber on 08/13/2020
 *
 * Copyright (c) 2020 MacPractice, Inc. All rights reserved.
 *
 * Description: Login screen
 */

import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { observer } from "mobx-react";

import {
  Button,
  Container,
  Grid,
  Link,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { useAppStore } from "stores/AppStore";
import backgroundImage from "images/login-bg.jpg";

const useStyles = makeStyles(theme => ({
  root: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: "flex",
    flexDirection: "column",
    background: `white url(${backgroundImage}) no-repeat left bottom`
  },
  center: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center"
  },
  version: {
    color: theme.palette.grey[700]
  }
}));

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { from } = location.state || { from: "/" };
  const app = useAppStore();

  const authenticate = async e => {
    e.preventDefault();
    await app.auth.authenticate(username, password);
    if (app.auth.isAuthenticated()) {
      history.replace(from);
    }
  };

  const error = (
    <Grid item>
      <Typography variant="body1" color="error">
        {app.auth.error}
      </Typography>
    </Grid>
  );

  return (
    <div className={classes.root}>
      <div className={classes.center}>
        <Container maxWidth="xs">
          <form method="post" onSubmit={authenticate}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Typography variant="h4" align="center">
                  {app.appName}
                </Typography>
              </Grid>
              {app.auth.error && error}
              <Grid item>
                <TextField
                  id="username"
                  label="Username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  variant="filled"
                  fullWidth
                />
              </Grid>
              <Grid item>
                <TextField
                  id="password"
                  type="password"
                  label="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  variant="filled"
                  fullWidth
                />
              </Grid>
              <Grid item>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  Log In
                </Button>
              </Grid>
              <Grid item>
                <Typography align="center" className={classes.version}>
                  Powered by{" "}
                  <Link
                    href="https://macpractice.com"
                    target="_blank"
                    rel="noopener"
                  >
                    MacPractice Inc.
                  </Link>
                </Typography>
                <Typography align="center" className={classes.version}>
                  Version {process.env.REACT_APP_VERSION}
                </Typography>
              </Grid>
            </Grid>
          </form>
        </Container>
      </div>
    </div>
  );
};

export default observer(Login);
